/* Thom Jensen Studio: hero words, scroll reveals, parallax, loupe, book tilt, dusk sky. */
(() => {
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const design = !!window.Shopify?.designMode;

  if (!reduce && !design && 'IntersectionObserver' in window) {
    root.classList.add('tj-js');
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('.tj-reveal').forEach(el => io.observe(el));
    // Safety: never leave content hidden.
    setTimeout(() => document.querySelectorAll('.tj-reveal').forEach(el => el.classList.add('is-in')), 4000);
  }

  // Scroll cue moves to the next section.
  document.querySelectorAll('[data-tj-scroll-next]').forEach(btn => btn.addEventListener('click', () => {
    const sec = btn.closest('.shopify-section') || btn.closest('section');
    const next = sec?.nextElementSibling;
    (next || sec).scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }));

  // Parallax + dusk sky, one rAF loop.
  const layers = reduce ? [] : [...document.querySelectorAll('[data-tj-parallax] [data-tj-depth]')];
  const dusks = [...document.querySelectorAll('[data-tj-dusk]')];
  const journeys = [...document.querySelectorAll('[data-tj-journey]')];
  let ticking = false;
  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    layers.forEach(el => {
      const box = el.closest('[data-tj-parallax]').getBoundingClientRect();
      if (box.bottom < -200 || box.top > vh + 200) return;
      const center = box.top + box.height / 2 - vh / 2;
      el.style.setProperty('--tj-shift', (-center * parseFloat(el.dataset.tjDepth)).toFixed(1) + 'px');
    });
    dusks.forEach(el => {
      const box = el.getBoundingClientRect();
      const p = reduce ? 0.65 : Math.min(1, Math.max(0, (vh - box.top) / (vh + box.height * 0.35)));
      el.style.setProperty('--p', p.toFixed(3));
    });
    journeys.forEach(el => {
      const track = el.querySelector('.tj-journey__track');
      if (!track) return;
      const box = track.getBoundingClientRect();
      const f = reduce ? 1 : Math.min(1, Math.max(0, (vh * 0.6 - box.top) / box.height));
      el.style.setProperty('--fill', f.toFixed(3));
    });
  };
  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  if (layers.length || dusks.length || journeys.length) {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  // Brushwork loupe. Initialised per figure, and again whenever the theme editor reloads a section.
  const initLoupe = fig => {
    if (fig.dataset.tjLoupeReady) return;
    const img = fig.querySelector('img');
    const lens = fig.querySelector('.tj-loupe__lens');
    if (!img || !lens) return;
    fig.dataset.tjLoupeReady = '1';
    const zoom = parseFloat(fig.dataset.zoom) || 3;
    const small = fig.dataset.small || img.currentSrc || img.src;
    const big = fig.dataset.big || small;
    let loaded = false;
    lens.style.backgroundImage = `url("${small}")`;
    const loadBig = () => {
      if (loaded) return; loaded = true;
      const hi = new Image();
      hi.onload = () => { lens.style.backgroundImage = `url("${big}")`; };
      hi.src = big;
    };
    // Mouse: lens sits on the cursor. Touch: lens floats above the finger so it stays visible.
    const move = (x, y, touch) => {
      const r = img.getBoundingClientRect();
      if (!r.width) return;
      const fx = Math.min(r.width, Math.max(0, x - r.left));
      const fy = Math.min(r.height, Math.max(0, y - r.top));
      const lw = lens.offsetWidth;
      const lift = touch ? lw * 0.85 : 0;
      lens.style.left = (fx + img.offsetLeft) + 'px';
      lens.style.top = (fy + img.offsetTop - lift) + 'px';
      lens.style.backgroundSize = `${r.width * zoom}px ${r.height * zoom}px`;
      lens.style.backgroundPosition = `${-(fx * zoom - lw / 2)}px ${-(fy * zoom - lw / 2)}px`;
    };
    // Touch: tap the painting to turn the magnifier on, drag to explore, tap Done to turn it off.
    let done = fig.querySelector('.tj-loupe__done');
    if (!done) {
      done = document.createElement('button');
      done.type = 'button';
      done.className = 'tj-loupe__done';
      done.textContent = 'Done';
      fig.appendChild(done);
    }
    const stopTouch = () => { fig.classList.remove('is-touch', 'is-looking'); };
    done.addEventListener('click', e => { e.stopPropagation(); stopTouch(); });
    done.addEventListener('pointerdown', e => e.stopPropagation());
    fig.addEventListener('pointerenter', loadBig);
    fig.addEventListener('pointerdown', e => {
      loadBig();
      if (e.pointerType === 'mouse') return;
      if (!fig.classList.contains('is-touch')) fig.classList.add('is-touch');
      fig.classList.add('is-looking');
      move(e.clientX, e.clientY, true);
    });
    fig.addEventListener('pointermove', e => {
      if (e.pointerType === 'mouse') { fig.classList.add('is-looking'); move(e.clientX, e.clientY, false); }
      else if (fig.classList.contains('is-touch')) move(e.clientX, e.clientY, true);
    });
    fig.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') fig.classList.remove('is-looking'); });
    if ('IntersectionObserver' in window) {
      const pre = new IntersectionObserver(es => { if (es[0].isIntersecting) { loadBig(); pre.disconnect(); } }, { rootMargin: '300px' });
      pre.observe(fig);
    }
  };
  const initAll = (root = document) => root.querySelectorAll('[data-tj-loupe]').forEach(initLoupe);
  initAll();
  document.addEventListener('shopify:section:load', e => {
    initAll(e.target);
    e.target.querySelectorAll('.tj-reveal').forEach(el => el.classList.add('is-in'));
  });
  // Room view wall colors.
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-tj-wall]');
    if (!b) return;
    const room = b.closest('.tj-story__panel')?.querySelector('[data-tj-room]');
    if (!room) return;
    room.style.setProperty('--wall', b.dataset.tjWall);
    room.style.setProperty('--sofa', b.dataset.sofa);
    room.style.setProperty('--sofa-dark', b.dataset.sofaDark);
    b.parentElement.querySelectorAll('button').forEach(x => { x.classList.toggle('is-active', x === b); x.setAttribute('aria-pressed', x === b); });
  });
  // Featured book: thumbnails swap the large image.
  document.addEventListener('click', e => {
    const t = e.target.closest('[data-tj-book-thumb]');
    if (!t) return;
    const wrap = t.closest('.tj-book__media');
    const main = wrap?.querySelector('.tj-book__cover');
    if (!main) return;
    main.removeAttribute('srcset');
    main.src = t.dataset.src;
    wrap.querySelectorAll('[data-tj-book-thumb]').forEach(b => b.classList.toggle('is-active', b === t));
  });
  // Product page: magnifier on the main gallery photos (mouse only; touch keeps Shopify's tap-to-zoom).
  const pdpLoupe = () => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.querySelectorAll('media-gallery .product-media-container--image').forEach(box => {
      if (box.dataset.tjPdpLoupe || box.closest('.dialog-zoomed-gallery, dialog')) return;
      const img = box.querySelector('img');
      if (!img) return;
      box.dataset.tjPdpLoupe = '1';
      box.classList.add('tj-pdp-loupe');
      const lens = document.createElement('span');
      lens.className = 'tj-loupe__lens tj-pdp-loupe__lens';
      lens.setAttribute('aria-hidden', 'true');
      box.appendChild(lens);
      const zoom = 2.6;
      let ready = false;
      const load = () => {
        if (ready) return; ready = true;
        let big = img.currentSrc || img.src;
        try { const u = new URL(big, location.href); u.searchParams.set('width', '2400'); big = u.toString(); } catch (e) {}
        lens.style.backgroundImage = `url("${img.currentSrc || img.src}")`;
        const hi = new Image(); hi.onload = () => { lens.style.backgroundImage = `url("${big}")`; }; hi.src = big;
      };
      box.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') load(); });
      box.addEventListener('pointermove', e => {
        if (e.pointerType !== 'mouse') return;
        const r = img.getBoundingClientRect(), b = box.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) { box.classList.remove('is-looking'); return; }
        const fx = e.clientX - r.left, fy = e.clientY - r.top, lw = lens.offsetWidth;
        box.classList.add('is-looking');
        lens.style.left = (e.clientX - b.left) + 'px';
        lens.style.top = (e.clientY - b.top) + 'px';
        lens.style.backgroundSize = `${r.width * zoom}px ${r.height * zoom}px`;
        lens.style.backgroundPosition = `${-(fx * zoom - lw / 2)}px ${-(fy * zoom - lw / 2)}px`;
      });
      box.addEventListener('pointerleave', () => box.classList.remove('is-looking'));
    });
  };
  // Room view on print pages: resize to the selected size option (e.g. "11 x 14").
  // Prints up to 20 inches wide switch to a closer picture-ledge scene; larger ones use the sofa.
  const roomSizes = () => document.querySelectorAll('[data-tj-size-from-variant]').forEach(room => {
    let sizes = {};
    try { sizes = JSON.parse(room.dataset.sizes || '{}'); } catch (e) { return; }
    const photo = parseFloat(room.dataset.photoRatio) || 1.25;
    const re = /(\d+(?:\.\d+)?)\s*(?:"|in|inch|inches)?\s*[x×X]\s*(\d+(?:\.\d+)?)/;
    const panel = room.closest('.tj-story__panel');
    const setSize = text => {
      const m = String(text || '').match(re);
      if (!m) return false;
      const a = parseFloat(m[1]), b = parseFloat(m[2]);
      const w = photo >= 1 ? Math.max(a, b) : Math.min(a, b);
      const h = photo >= 1 ? Math.min(a, b) : Math.max(a, b);
      const ledge = Math.max(w, h) < 20;
      room.dataset.scene = ledge ? 'ledge' : 'sofa';
      room.style.setProperty('--w', w);
      room.style.setProperty('--ratio', (w / h).toFixed(4));
      const size = panel?.querySelector('[data-tj-room-size]');
      if (size) size.textContent = `${w} × ${h}`;
      const scene = panel?.querySelector('[data-tj-room-scene]');
      if (scene) scene.textContent = ledge ? 'on a 36-inch picture ledge' : 'above a 62-inch credenza';
      return true;
    };
    const fromVariant = id => id && setSize(sizes[String(id)]);
    const currentId = () => new URL(location.href).searchParams.get('variant')
      || document.querySelector('form[action*="/cart/add"] [name="id"]')?.value
      || room.dataset.current;
    fromVariant(currentId());
    document.addEventListener('change', e => {
      const t = e.target;
      if (!t || !t.closest('variant-picker, .variant-picker, form[action*="/cart/add"], product-form')) return;
      const picked = t.tagName === 'SELECT' ? t.options[t.selectedIndex]?.text : t.value;
      if (!setSize(picked)) [80, 350, 900].forEach(ms => setTimeout(() => fromVariant(currentId()), ms));
    });
    document.addEventListener('variant:update', e => {
      const id = e?.detail?.resource?.id;
      if (!fromVariant(id)) [80, 350].forEach(ms => setTimeout(() => fromVariant(currentId()), ms));
    });
    window.addEventListener('popstate', () => fromVariant(currentId()));
  });
  roomSizes();
 
})();
